import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Label,
  Form,
  FormGroup,
  Row,
  Col,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { toast } from "react-toastify";

const ModalMedicamentosFactura = ({
  isOpen,
  toggle,
  medicamentos = [],
  onSave,
  readOnly = false,
  facturaNumero = "",
  // Campos de factura editables (opcional)
  mostrarCamposFactura = false,
  fechaFactura = "",
  montoTotal = "",
  onFechaChange = null,
  onNumeroChange = null,
  onMontoChange = null,
}) => {
  const [medicamentosEditables, setMedicamentosEditables] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [nuevoMedicamento, setNuevoMedicamento] = useState({
    nombre: "",
    nombre_completo: "",
    dosis: "",
    unidad_dosis: "MG",
    forma_farmaceutica: "",
    cantidad_unidades: "",
    cantidad_comprada: 1,
    laboratorio: "",
    marca: "",
    tipo: "",
    precio_total: "",
    extraido_con_ia: false,
  });

  useEffect(() => {
    if (isOpen) {
      // Asegurar que unidad_dosis tenga un valor por defecto si no existe
      const medicamentosConDefaults = medicamentos.map(med => ({
        ...med,
        unidad_dosis: med.unidad_dosis || "MG"
      }));
      setMedicamentosEditables(medicamentosConDefaults);
      setEditingIndex(null);
      setShowAddForm(false);
    }
  }, [isOpen, medicamentos]);

  const handleEdit = (index) => {
    setEditingIndex(index);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setMedicamentosEditables([...medicamentos]);
  };

  const handleSaveEdit = (index) => {
    setEditingIndex(null);
  };

  const handleChange = (index, field, value) => {
    const updated = [...medicamentosEditables];
    const med = { ...updated[index], [field]: value };

    // Lógica de cálculo automático
    if (field === "cantidad_comprada") {
      // Si cambia cantidad -> Recalcular Total (Total = Cant * Unitario)
      const cantidad = parseFloat(value) || 0;
      const unitario = parseFloat(med.precio_unitario) || 0;
      // Solo recalcular si hay un precio unitario válido
      if (unitario > 0) {
        med.precio_total = (cantidad * unitario).toFixed(2);
      }
    } else if (field === "precio_total") {
      // Si cambia Total -> Recalcular Unitario implícito (Unitario = Total / Cant)
      // Esto asegura que si luego cambian la cantidad, se use el nuevo precio base
      const total = parseFloat(value) || 0;
      const cantidad = parseFloat(med.cantidad_comprada) || 1;
      if (cantidad > 0) {
        med.precio_unitario = total / cantidad;
      }
    }

    updated[index] = med;
    setMedicamentosEditables(updated);
  };

  const handleDelete = (index) => {
    if (window.confirm("¿Está seguro de eliminar este medicamento?")) {
      const updated = medicamentosEditables.filter((_, i) => i !== index);
      setMedicamentosEditables(updated);
      toast.success("Medicamento eliminado");
    }
  };

  const handleAddNew = () => {
    setShowAddForm(true);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNuevoMedicamento({
      nombre: "",
      nombre_completo: "",
      dosis: "",
      unidad_dosis: "MG",
      forma_farmaceutica: "",
      cantidad_unidades: "",
      cantidad_comprada: 1,
      laboratorio: "",
      marca: "",
      tipo: "",
      precio_total: "",
      extraido_con_ia: false,
    });
  };

  const handleSaveNew = () => {
    // Validar campos requeridos
    if (!nuevoMedicamento.nombre || !nuevoMedicamento.precio_total) {
      toast.error("El nombre y precio son requeridos");
      return;
    }

    const medicamentoCompleto = {
      ...nuevoMedicamento,
      precio_total: parseFloat(String(nuevoMedicamento.precio_total).replace(',', '.')),
      cantidad_unidades: nuevoMedicamento.cantidad_unidades
        ? parseInt(nuevoMedicamento.cantidad_unidades)
        : null,
      cantidad_comprada: parseInt(nuevoMedicamento.cantidad_comprada) || 1,
    };

    setMedicamentosEditables([...medicamentosEditables, medicamentoCompleto]);
    handleCancelAdd();
    toast.success("Medicamento agregado");
  };

  const handleSaveAll = () => {
    // Normalizar precio_total a número (coma decimal → punto)
    const medicamentosNormalizados = medicamentosEditables.map(med => ({
      ...med,
      precio_total: typeof med.precio_total === 'string'
        ? parseFloat(med.precio_total.replace(',', '.')) || 0
        : parseFloat(med.precio_total) || 0,
    }));
    onSave(medicamentosNormalizados);
    toggle();
    toast.success("Medicamentos actualizados exitosamente");
  };

  const calcularTotal = () => {
    return medicamentosEditables.reduce(
      (sum, med) => sum + (parseFloat(med.precio_total) || 0),
      0
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable className="modal-medicamentos">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <i className="ri-medicine-bottle-line me-2 text-primary fs-20"></i>
          <div>
            <h5 className="mb-0">Medicamentos de Factura</h5>
            {facturaNumero && (
              <small className="text-muted">Factura #{facturaNumero}</small>
            )}
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Campos de factura editables (si se pasan como props) */}
        {mostrarCamposFactura && (
          <div className="mb-4 p-3 border rounded bg-light">
            <h6 className="fs-14 mb-3">
              <i className="ri-file-text-line me-2"></i>
              Datos de la Factura
            </h6>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="fecha-factura">Fecha</Label>
                  <Input
                    type="date"
                    id="fecha-factura"
                    value={fechaFactura}
                    onChange={(e) => onFechaChange && onFechaChange(e.target.value)}
                    disabled={readOnly}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="numero-factura">Número de Factura</Label>
                  <Input
                    type="text"
                    id="numero-factura"
                    value={facturaNumero}
                    onChange={(e) => onNumeroChange && onNumeroChange(e.target.value)}
                    placeholder="Ej: 00123456"
                    disabled={readOnly}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="monto-total">Monto Total (Bs)</Label>
                  <Input
                    type="text"
                    id="monto-total"
                    value={montoTotal}
                    onChange={(e) => onMontoChange && onMontoChange(e.target.value)}
                    placeholder="Ej: 1234,56"
                    disabled={readOnly}
                  />
                </FormGroup>
              </Col>
            </Row>
          </div>
        )}

        {medicamentosEditables.length === 0 && !showAddForm ? (
          <div className="text-center py-5">
            <i className="ri-medicine-bottle-line text-muted" style={{ fontSize: "48px" }}></i>
            <p className="text-muted mt-3">No hay medicamentos registrados</p>
            {!readOnly && (
              <Button color="primary" onClick={handleAddNew}>
                <i className="ri-add-line me-1"></i>
                Agregar Medicamento
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" size="sm">
                <thead className="table-light">
                  <tr>
                    <th className="text-center" style={{ width: "3%" }}>#</th>
                    <th style={{ width: "22%" }}>Medicamento</th>
                    <th style={{ width: "13%" }}>Presentación</th>
                    <th style={{ width: "13%" }}>Laboratorio</th>
                    <th className="text-center" style={{ width: "10%" }}>Tipo</th>
                    <th className="text-center" style={{ width: "6%" }}>Cant.</th>
                    <th className="text-end" style={{ width: "13%" }}>Precio Total</th>
                    {!readOnly && <th className="text-center" style={{ width: "10%" }}>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {medicamentosEditables.map((med, index) => {
                    // Armar nombre_completo automáticamente
                    const nombreCompleto = [
                      med.nombre,
                      med.dosis && med.unidad_dosis ? `${med.dosis}${med.unidad_dosis}` : '',
                      med.forma_farmaceutica && med.cantidad_unidades ? `X ${med.cantidad_unidades}${med.forma_farmaceutica}` : '',
                      med.laboratorio ? `(${med.laboratorio})` : ''
                    ].filter(Boolean).join(' ');

                    // Actualizar nombre_completo en el objeto
                    if (med.nombre_completo !== nombreCompleto) {
                      med.nombre_completo = nombreCompleto;
                    }

                    return (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          {editingIndex === index ? (
                            <div>
                              <Input
                                type="text"
                                bsSize="sm"
                                value={med.nombre}
                                onChange={(e) =>
                                  handleChange(index, "nombre", e.target.value)
                                }
                                placeholder="Nombre"
                              />
                              {nombreCompleto && (
                                <small className="text-muted d-block mt-1">
                                  {nombreCompleto}
                                </small>
                              )}
                            </div>
                          ) : (
                            <div>
                              <div className="fw-medium">{med.nombre}</div>
                              {nombreCompleto && (
                                <small className="text-muted d-block">
                                  {nombreCompleto}
                                </small>
                              )}
                              {med.extraido_con_ia && (
                                <Badge
                                  color="info"
                                  className="ms-2"
                                  id={`badge-ia-${index}`}
                                >
                                  <i className="ri-robot-line me-1"></i>IA
                                  <UncontrolledTooltip
                                    target={`badge-ia-${index}`}
                                    placement="top"
                                  >
                                    Extraído con IA
                                    {med.confianza_extraccion &&
                                      ` (${(med.confianza_extraccion * 100).toFixed(0)}% confianza)`}
                                  </UncontrolledTooltip>
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                      <td>
                        {editingIndex === index ? (
                          <div>
                            <div className="input-group input-group-sm mb-1">
                              <Input
                                type="text"
                                bsSize="sm"
                                value={med.dosis || ""}
                                onChange={(e) =>
                                  handleChange(index, "dosis", e.target.value)
                                }
                                placeholder="Dosis"
                                style={{ maxWidth: "60px" }}
                              />
                              <Input
                                type="select"
                                bsSize="sm"
                                value={med.unidad_dosis || ""}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    "unidad_dosis",
                                    e.target.value
                                  )
                                }
                                style={{ maxWidth: "70px" }}
                              >
                                <option value="">Unidad</option>
                                <option value="MG">MG</option>
                                <option value="MCG">MCG</option>
                                <option value="G">G</option>
                                <option value="ML">ML</option>
                                <option value="UI">UI</option>
                              </Input>
                            </div>
                            <div className="input-group input-group-sm">
                              <Input
                                type="text"
                                bsSize="sm"
                                value={med.forma_farmaceutica || ""}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    "forma_farmaceutica",
                                    e.target.value
                                  )
                                }
                                placeholder="Forma"
                                style={{ maxWidth: "70px" }}
                              />
                              <span className="input-group-text">X</span>
                              <Input
                                type="number"
                                bsSize="sm"
                                value={med.cantidad_unidades || ""}
                                onChange={(e) =>
                                  handleChange(
                                    index,
                                    "cantidad_unidades",
                                    e.target.value
                                  )
                                }
                                placeholder="#"
                                style={{ maxWidth: "50px" }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div>
                            {med.dosis && (
                              <div className="fw-medium">
                                {med.dosis}{med.unidad_dosis || ''}
                              </div>
                            )}
                            {med.forma_farmaceutica && (
                              <small className="text-muted d-block">
                                {med.forma_farmaceutica}
                                {med.cantidad_unidades &&
                                  ` X ${med.cantidad_unidades}`}
                              </small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {editingIndex === index ? (
                          <div>
                            <Input
                              type="text"
                              bsSize="sm"
                              value={med.laboratorio || ""}
                              onChange={(e) =>
                                handleChange(
                                  index,
                                  "laboratorio",
                                  e.target.value
                                )
                              }
                              placeholder="Laboratorio"
                            />
                            <Input
                              type="text"
                              bsSize="sm"
                              value={med.marca || ""}
                              onChange={(e) =>
                                handleChange(index, "marca", e.target.value)
                              }
                              placeholder="Marca"
                              className="mt-1"
                            />
                          </div>
                        ) : (
                          <div>
                            {med.laboratorio && (
                              <div className="fw-medium">{med.laboratorio}</div>
                            )}
                            {med.marca && (
                              <small className="text-muted">{med.marca}</small>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {editingIndex === index ? (
                          <Input
                            type="select"
                            bsSize="sm"
                            value={med.tipo || ""}
                            onChange={(e) =>
                              handleChange(index, "tipo", e.target.value)
                            }
                          >
                            <option value="">Seleccionar</option>
                            <option value="Bioequivalente">Bioequivalente</option>
                            <option value="De Marca">De Marca</option>
                          </Input>
                        ) : (
                          <div>
                            {med.tipo && (
                              <Badge
                                color={
                                  med.tipo === "Bioequivalente"
                                    ? "success"
                                    : med.tipo === "De Marca"
                                    ? "primary"
                                    : "secondary"
                                }
                                className="badge-soft"
                              >
                                {med.tipo}
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        {editingIndex === index ? (
                          <Input
                            type="number"
                            bsSize="sm"
                            value={med.cantidad_comprada || 1}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "cantidad_comprada",
                                e.target.value
                              )
                            }
                            min="1"
                          />
                        ) : (
                          <span>{med.cantidad_comprada || 1}</span>
                        )}
                      </td>
                      <td className="text-end">
                        {editingIndex === index ? (
                          <Input
                            type="text"
                            bsSize="sm"
                            value={med.precio_total}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "precio_total",
                                e.target.value
                              )
                            }
                            placeholder="0,00"
                          />
                        ) : (
                          <span className="fw-medium text-primary">
                            {new Intl.NumberFormat("es-VE", {
                              style: "decimal",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(parseFloat(med.precio_total) || 0)}{" "}
                            Bs
                          </span>
                        )}
                      </td>
                      {!readOnly && (
                        <td className="text-center">
                          {editingIndex === index ? (
                            <div className="d-flex gap-1 justify-content-center">
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => handleSaveEdit(index)}
                              >
                                <i className="ri-check-line"></i>
                              </Button>
                              <Button
                                color="secondary"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex gap-1 justify-content-center">
                              <Button
                                color="soft-primary"
                                size="sm"
                                onClick={() => handleEdit(index)}
                              >
                                <i className="ri-pencil-line"></i>
                              </Button>
                              <Button
                                color="soft-danger"
                                size="sm"
                                onClick={() => handleDelete(index)}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                </tbody>
              </Table>
            </div>

            {/* Formulario para agregar nuevo medicamento */}
            {showAddForm && (
              <div className="border rounded p-3 mt-3 bg-light">
                <h6 className="mb-3">
                  <i className="ri-add-circle-line me-2"></i>
                  Nuevo Medicamento
                </h6>
                <Form>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          Nombre <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.nombre}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              nombre: e.target.value,
                            })
                          }
                          placeholder="Ej: DIHIDROLIP"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Nombre Completo</Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.nombre_completo}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              nombre_completo: e.target.value,
                            })
                          }
                          placeholder="Ej: DIHIDROLIP 10MG X 10COMP"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Dosis</Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.dosis}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              dosis: e.target.value,
                            })
                          }
                          placeholder="Ej: 10"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Unidad</Label>
                        <Input
                          type="select"
                          value={nuevoMedicamento.unidad_dosis}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              unidad_dosis: e.target.value,
                            })
                          }
                        >
                          <option value="MG">MG</option>
                          <option value="G">G</option>
                          <option value="ML">ML</option>
                          <option value="MCG">MCG</option>
                          <option value="UI">UI</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Forma</Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.forma_farmaceutica}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              forma_farmaceutica: e.target.value,
                            })
                          }
                          placeholder="COMP, TAB, CAP"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Unidades</Label>
                        <Input
                          type="number"
                          value={nuevoMedicamento.cantidad_unidades}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              cantidad_unidades: e.target.value,
                            })
                          }
                          placeholder="10, 30, 60"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Laboratorio</Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.laboratorio}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              laboratorio: e.target.value,
                            })
                          }
                          placeholder="Ej: VARGAS, PFIZER"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Marca</Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.marca}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              marca: e.target.value,
                            })
                          }
                          placeholder="Marca del laboratorio"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Tipo</Label>
                        <Input
                          type="select"
                          value={nuevoMedicamento.tipo}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              tipo: e.target.value,
                            })
                          }
                        >
                          <option value="">Seleccionar</option>
                          <option value="Bioequivalente">Bioequivalente</option>
                          <option value="De Marca">De Marca</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Cantidad Comprada</Label>
                        <Input
                          type="number"
                          value={nuevoMedicamento.cantidad_comprada}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              cantidad_comprada: e.target.value,
                            })
                          }
                          min="1"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          Precio Total (Bs){" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={nuevoMedicamento.precio_total}
                          onChange={(e) =>
                            setNuevoMedicamento({
                              ...nuevoMedicamento,
                              precio_total: e.target.value,
                            })
                          }
                          placeholder="0,00"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 justify-content-end">
                    <Button color="secondary" onClick={handleCancelAdd}>
                      <i className="ri-close-line me-1"></i>
                      Cancelar
                    </Button>
                    <Button color="success" onClick={handleSaveNew}>
                      <i className="ri-check-line me-1"></i>
                      Guardar
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {/* Botón para agregar nuevo */}
            {!showAddForm && !readOnly && (
              <div className="mt-3">
                <Button color="primary" outline onClick={handleAddNew}>
                  <i className="ri-add-line me-1"></i>
                  Agregar Medicamento
                </Button>
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {readOnly ? "Cerrar" : "Cancelar"}
        </Button>
        {!readOnly && medicamentosEditables.length > 0 && (
          <Button color="primary" onClick={handleSaveAll}>
            <i className="ri-save-line me-1"></i>
            Guardar Cambios
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ModalMedicamentosFactura;
